import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  loginRequest,
  logoutRequest,
  mfaChallenge,
  mfaVerifySetup,
  refreshRequest,
  type AuthChallenge,
  type LoginResult,
  type TokenBundle,
} from "./auth-api.js";
import { getAuthMode, type AuthMode } from "./config.js";
import { bindAuthHttpHandlers } from "./http.js";
import {
  clearChallenge,
  clearTokens,
  getAccessExpiresAt,
  getAccessToken,
  getRefreshToken,
  setChallenge,
  setTokens,
} from "./token-storage.js";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

type AuthContextValue = {
  mode: AuthMode;
  status: AuthStatus;
  /** true após logout forçado por refresh/401 */
  sessionExpired: boolean;
  clearSessionExpired: () => void;
  login: (email: string, password: string) => Promise<LoginResult>;
  completeMfaChallenge: (input: {
    email: string;
    session: string;
    code: string;
  }) => Promise<void>;
  completeMfaSetup: (input: {
    email: string;
    session: string;
    code: string;
  }) => Promise<string[]>;
  applyTokens: (tokens: TokenBundle) => void;
  beginChallenge: (email: string, challenge: AuthChallenge) => void;
  refreshSession: () => Promise<boolean>;
  logout: (opts?: { global?: boolean; reason?: "logout" | "expired" }) => Promise<void>;
  getAccessToken: () => string | null;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function applyBundle(tokens: TokenBundle): void {
  setTokens(tokens);
  clearChallenge();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const mode = getAuthMode();
  const [status, setStatus] = useState<AuthStatus>(
    mode === "demo" ? "authenticated" : "loading",
  );
  const [sessionExpired, setSessionExpired] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const bootstrapped = useRef(false);
  const loggingOut = useRef(false);

  const bump = useCallback(() => setEpoch((e) => e + 1), []);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    if (mode !== "api") return true;
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    try {
      const tokens = await refreshRequest(refreshToken);
      applyBundle(tokens);
      setStatus("authenticated");
      bump();
      return true;
    } catch {
      // Não apagar tokens se o login/MFA acabou de gravar outra sessão.
      if (getRefreshToken() === refreshToken) {
        clearTokens();
        setStatus("anonymous");
        bump();
      }
      return false;
    }
  }, [bump, mode]);

  const logout = useCallback(
    async (opts?: { global?: boolean; reason?: "logout" | "expired" }) => {
      if (mode === "demo") return;
      if (loggingOut.current) return;
      loggingOut.current = true;
      try {
        const refreshToken = getRefreshToken();
        const accessToken = getAccessToken();
        try {
          await logoutRequest({
            refreshToken,
            accessToken,
            global: opts?.global === true,
          });
        } catch {
          /* logout é best-effort */
        }
        clearTokens();
        clearChallenge();
        setStatus("anonymous");
        if (opts?.reason === "expired") {
          setSessionExpired(true);
        }
        bump();
      } finally {
        loggingOut.current = false;
      }
    },
    [bump, mode],
  );

  useEffect(() => {
    if (mode !== "api") return;
    bindAuthHttpHandlers({
      refresh: refreshSession,
      onSessionLost: () => {
        void logout({ reason: "expired" });
      },
    });
  }, [logout, mode, refreshSession]);

  useEffect(() => {
    if (mode !== "api" || bootstrapped.current) return;
    bootstrapped.current = true;
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      setStatus("anonymous");
      return;
    }
    void (async () => {
      const ok = await refreshSession();
      if (!ok && !getAccessToken()) {
        setStatus("anonymous");
      }
    })();
  }, [mode, refreshSession]);

  // Renovação silenciosa ~60s antes do expiry
  useEffect(() => {
    if (mode !== "api" || status !== "authenticated") return;
    const expiresAt = getAccessExpiresAt();
    if (!expiresAt) return;
    const delay = Math.max(expiresAt - Date.now() - 60_000, 5_000);
    const id = window.setTimeout(() => {
      void refreshSession().then((ok) => {
        if (!ok) void logout({ reason: "expired" });
      });
    }, delay);
    return () => window.clearTimeout(id);
  }, [epoch, logout, mode, refreshSession, status]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const result = await loginRequest(email, password);
      if (result.kind === "tokens") {
        applyBundle(result.tokens);
        setSessionExpired(false);
        setStatus("authenticated");
        bump();
      } else {
        setChallenge({
          email,
          session: result.challenge.session,
          challengeName: result.challenge.challengeName,
        });
      }
      return result;
    },
    [bump],
  );

  const applyTokens = useCallback(
    (tokens: TokenBundle) => {
      applyBundle(tokens);
      setSessionExpired(false);
      setStatus("authenticated");
      bump();
    },
    [bump],
  );

  const beginChallenge = useCallback((email: string, challenge: AuthChallenge) => {
    setChallenge({
      email,
      session: challenge.session,
      challengeName: challenge.challengeName,
    });
  }, []);

  const completeMfaChallenge = useCallback(
    async (input: { email: string; session: string; code: string }) => {
      const tokens = await mfaChallenge(input);
      applyTokens(tokens);
    },
    [applyTokens],
  );

  const completeMfaSetup = useCallback(
    async (input: { email: string; session: string; code: string }) => {
      const result = await mfaVerifySetup(input);
      const { recoveryCodes, ...tokens } = result;
      applyTokens(tokens);
      return recoveryCodes;
    },
    [applyTokens],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      mode,
      status,
      sessionExpired,
      clearSessionExpired: () => setSessionExpired(false),
      login,
      completeMfaChallenge,
      completeMfaSetup,
      applyTokens,
      beginChallenge,
      refreshSession,
      logout,
      getAccessToken,
    }),
    [
      applyTokens,
      beginChallenge,
      completeMfaChallenge,
      completeMfaSetup,
      login,
      logout,
      mode,
      refreshSession,
      sessionExpired,
      status,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth fora de AuthProvider");
  return ctx;
}
